import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Chip, TextField, MenuItem, Card, CardContent,
  Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  IconButton, Tooltip, CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility, Refresh } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { adminService } from '../../services/adminService';
import dayjs from 'dayjs';

const statusColor = { success: 'success', failed: 'error', skipped: 'warning' };

const StatCard = ({ label, value, color }) => (
  <Card variant="outlined">
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="h4" color={color || 'text.primary'} fontWeight={700}>
        {value ?? '—'}
      </Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </CardContent>
  </Card>
);

const AiLogList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailLog, setDetailLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminService.getAiLogStats();
      setStats(res.data.stats);
    } catch {
      // stats are optional
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getAiLogs({
        page: paginationModel.page + 1,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      const data = res.data.logs;
      setLogs(data.data || []);
      setRowCount(data.total || 0);
    } catch {
      enqueueSnackbar('Failed to load AI logs', { variant: 'error' });
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, search, statusFilter, enqueueSnackbar]);

  useEffect(() => { fetchLogs(); fetchStats(); }, [fetchLogs, fetchStats]);

  const handleViewDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailLog(null);
    try {
      const res = await adminService.getAiLog(id);
      setDetailLog(res.data.log);
    } catch {
      enqueueSnackbar('Failed to load log detail', { variant: 'error' });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      field: 'created_at',
      headerName: 'Time',
      width: 160,
      valueFormatter: (v) => dayjs(v).format('MMM D, YYYY HH:mm'),
    },
    { field: 'search_query', headerName: 'Search Query', flex: 1, minWidth: 160 },
    {
      field: 'location_name',
      headerName: 'Location',
      width: 140,
      valueFormatter: (v) => v || '—',
    },
    {
      field: 'category_name',
      headerName: 'Category',
      width: 140,
      valueFormatter: (v) => v || '—',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: ({ value }) => (
        <Chip label={value} color={statusColor[value] || 'default'} size="small" />
      ),
    },
    {
      field: 'jobs_generated',
      headerName: 'Jobs Created',
      width: 110,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'duration_ms',
      headerName: 'Duration',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => (v != null ? `${v} ms` : '—'),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="View details">
          <IconButton size="small" onClick={() => handleViewDetail(row.id)}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>AI Logs</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => { fetchLogs(); fetchStats(); }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Total Calls" value={stats.total} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Success" value={stats.success} color="success.main" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Failed" value={stats.failed} color="error.main" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Skipped" value={stats.skipped} color="warning.main" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Jobs Created" value={stats.total_jobs} color="primary.main" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              label="Avg Response"
              value={stats.avg_duration_ms != null ? `${stats.avg_duration_ms} ms` : null}
            />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Search query"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPaginationModel(m => ({ ...m, page: 0 })); }}
          sx={{ width: 280 }}
          size="small"
        />
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPaginationModel(m => ({ ...m, page: 0 })); }}
          sx={{ width: 140 }}
          size="small"
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="success">Success</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
          <MenuItem value="skipped">Skipped</MenuItem>
        </TextField>
      </Box>

      <Box sx={{ height: 580, width: '100%' }}>
        <DataGrid
          rows={logs}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          pageSizeOptions={[20]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
        />
      </Box>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>AI Log Detail</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailLog ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Search Query</Typography>
                  <Typography fontWeight={600}>{detailLog.search_query}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box>
                    <Chip
                      label={detailLog.status}
                      color={statusColor[detailLog.status] || 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Jobs Created</Typography>
                  <Typography fontWeight={600}>{detailLog.jobs_generated}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                  <Typography>{detailLog.location_name || '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography>{detailLog.category_name || '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Duration</Typography>
                  <Typography>{detailLog.duration_ms != null ? `${detailLog.duration_ms} ms` : '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Time</Typography>
                  <Typography>{dayjs(detailLog.created_at).format('MMM D, YYYY HH:mm:ss')}</Typography>
                </Grid>
              </Grid>

              {detailLog.error_message && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Error</Typography>
                  <Box sx={{ bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200', borderRadius: 1, p: 1.5, mt: 0.5 }}>
                    <Typography variant="body2" color="error.dark" fontFamily="monospace" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {detailLog.error_message}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box>
                <Typography variant="caption" color="text.secondary">Prompt Sent to OpenAI</Typography>
                <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 1.5, mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="body2" fontFamily="monospace" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {detailLog.prompt_sent}
                  </Typography>
                </Box>
              </Box>

              {detailLog.response_raw && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Raw Response from OpenAI</Typography>
                  <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 1.5, mt: 0.5, maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="body2" fontFamily="monospace" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {(() => {
                        try { return JSON.stringify(JSON.parse(detailLog.response_raw), null, 2); }
                        catch { return detailLog.response_raw; }
                      })()}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiLogList;
